import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeftRight, Plus, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { exportToExcel, exportToPDF } from "@/lib/exports";

export default function Transfers() {
  const { user } = useAuth();
  const [transfers, setTransfers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [receiving, setReceiving] = useState(null);

  const canInitiate = user?.role === "admin" || user?.role === "pdi";
  const canReceive = (t) =>
    t.status === "in_transit" &&
    (user?.role === "admin" || (user?.role === "branch" && user?.branch === t.to_branch));

  const fetchAll = async () => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const { data } = await api.get("/transfers", { params });
    setTransfers(data);
  };

  const refreshVehicles = () => api.get("/transfers/available-vehicles").then(({ data }) => setVehicles(data));

  useEffect(() => { fetchAll(); /* eslint-disable-line */ }, [dateFrom, dateTo]);
  useEffect(() => {
    api.get("/branches").then(({ data }) => setBranches(data.branches));
    refreshVehicles();
  }, []);

  const columns = [
    { key: "_sr", label: "SR. No." },
    { key: "created_at", label: "Dispatched", fmt: (v) => new Date(v).toLocaleString("en-IN") },
    { key: "model", label: "Model" },
    { key: "chassis_number", label: "Chassis" },
    { key: "from_branch", label: "From" },
    { key: "to_branch", label: "To" },
    { key: "transferred_by", label: "Dispatched By" },
    { key: "status", label: "Status" },
    { key: "received_by", label: "Received By" },
    { key: "date_of_delivery", label: "Date of Delivery",
      fmt: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—" },
    { key: "financer_name", label: "Financer", fmt: (v) => v || "—" },
    { key: "received_remarks", label: "Received Remarks" },
  ];
  const transfersWithSr = transfers.map((t, i) => ({ ...t, _sr: i + 1 }));

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="transfers-heading">Vehicle Transfer Register</h1>
          <p className="text-sm text-slate-500 mt-1">PDI dispatches, branches receive — permanent audit trail</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="export-transfers-excel" onClick={() => exportToExcel("transfers", transfersWithSr, columns)}
            className="px-3 py-2 rounded-md border border-slate-300 hover:bg-slate-50 text-sm">Excel</button>
          <button data-testid="export-transfers-pdf" onClick={() => exportToPDF("Vehicle Transfers", transfersWithSr, columns)}
            className="px-3 py-2 rounded-md border border-slate-300 hover:bg-slate-50 text-sm">PDF</button>
          {canInitiate && (
            <button data-testid="new-transfer-button" onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-orange bg-brand-orange-hover text-white text-sm">
              <Plus className="w-4 h-4" /> New Transfer
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 flex gap-3">
        <label className="text-sm">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">From</div>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="transfer-date-from"
            className="px-3 py-2 rounded-md border border-slate-300 text-sm" />
        </label>
        <label className="text-sm">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">To</div>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="transfer-date-to"
            className="px-3 py-2 rounded-md border border-slate-300 text-sm" />
        </label>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg table-scroll">
        <table className="data-table w-full" data-testid="transfers-table">
          <thead>
            <tr>
              <th className="col-sr">SR. No.</th>
              <th>Dispatched</th><th>Model</th><th>Chassis</th>
              <th>From</th><th>To</th><th>By</th><th>Status</th><th>Received</th>
              <th>Delivered</th>
              <th className="text-right pr-6">Action</th>
            </tr>
          </thead>
          <tbody>
            {transfers.length === 0 && (
              <tr><td colSpan={11} className="text-center py-10 text-slate-400 text-sm">No transfers recorded yet</td></tr>
            )}
            {transfers.map((t, idx) => (
              <tr key={t.id} data-testid={`transfer-row-${t.id}`}>
                <td className="col-sr">{idx + 1}</td>
                <td className="text-xs text-slate-500">{new Date(t.created_at).toLocaleString("en-IN")}</td>
                <td className="font-medium text-slate-900">{t.model}</td>
                <td className="font-mono text-xs">{t.chassis_number}</td>
                <td>{t.from_branch}</td>
                <td><span className="inline-flex items-center gap-1 text-orange-700">
                  <ArrowLeftRight className="w-3 h-3" />{t.to_branch}
                </span></td>
                <td className="text-xs">{t.transferred_by}</td>
                <td>
                  {t.status === "received" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800" data-testid={`transfer-status-${t.id}`}>
                      <CheckCircle2 className="w-3 h-3" /> Received
                    </span>
                  ) : t.status === "cancelled" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600" data-testid={`transfer-status-${t.id}`}>
                      Cancelled
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800" data-testid={`transfer-status-${t.id}`}>
                      <Clock className="w-3 h-3" /> In Transit
                    </span>
                  )}
                </td>
                <td className="text-xs">
                  {t.received_by ? (
                    <>
                      <div>{t.received_by}</div>
                      {t.received_remarks && <div className="text-slate-500">{t.received_remarks}</div>}
                    </>
                  ) : "—"}
                </td>
                <td className="text-xs text-slate-500" data-testid={`transfer-dod-${t.id}`}>
                  {t.date_of_delivery
                    ? new Date(t.date_of_delivery).toLocaleDateString("en-IN")
                    : "—"}
                  {t.financer_name && (
                    <div className="text-[10px] text-slate-400">{t.financer_name}</div>
                  )}
                </td>
                <td className="text-right pr-6">
                  {canReceive(t) && (
                    <button data-testid={`receive-transfer-${t.id}`}
                      onClick={() => setReceiving(t)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
                      <CheckCircle2 className="w-3 h-3" /> Receive
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <TransferForm
          vehicles={vehicles}
          branches={branches}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); fetchAll(); refreshVehicles(); }}
        />
      )}
      {receiving && (
        <ReceiveForm
          transfer={receiving}
          onClose={() => setReceiving(null)}
          onSaved={() => { setReceiving(null); fetchAll(); refreshVehicles(); }}
        />
      )}
    </div>
  );
}

function TransferForm({ vehicles, branches, onClose, onSaved }) {
  const [vehicleId, setVehicleId] = useState(vehicles[0]?.id || "");
  const [toBranch, setToBranch] = useState(branches[0] || "");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  const sel = vehicles.find(v => v.id === vehicleId);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/transfers", { vehicle_id: vehicleId, to_branch: toBranch, remarks });
      toast.success("Transfer dispatched — awaiting receipt at destination");
      onSaved();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="transfer-form-modal">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
        <div className="font-heading text-xl font-semibold mb-4">Dispatch Vehicle Transfer</div>
        <label className="block mb-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Vehicle</div>
          <select required value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}
            className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm bg-white" data-testid="transfer-vehicle-select">
            {vehicles.map(v => <option key={v.id} value={v.id}>{v.model} · {v.chassis_number} · {v.branch}</option>)}
          </select>
        </label>
        {sel && (
          <div className="text-xs text-slate-500 mb-3 bg-slate-50 rounded-md p-3">
            Current branch: <span className="font-semibold text-slate-800">{sel.branch}</span>
          </div>
        )}
        <label className="block mb-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">To Branch</div>
          <select required value={toBranch} onChange={(e) => setToBranch(e.target.value)}
            className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm bg-white" data-testid="transfer-tobranch-select">
            {branches.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>
        <label className="block mb-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Dispatch Remarks</div>
          <input value={remarks} onChange={(e) => setRemarks(e.target.value)}
            placeholder="e.g. Priority delivery, transporter details…"
            className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm" data-testid="transfer-remarks-input" />
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-sm border border-slate-300" data-testid="transfer-cancel">Cancel</button>
          <button type="submit" disabled={saving} data-testid="transfer-submit"
            className="px-4 py-2 rounded-md text-sm bg-brand-blue bg-brand-blue-hover text-white disabled:opacity-60">
            {saving ? "Dispatching…" : "Dispatch Transfer"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ReceiveForm({ transfer, onClose, onSaved }) {
  const [receivedBy, setReceivedBy] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/transfers/${transfer.id}/receive`, {
        received_by: receivedBy,
        received_remarks: remarks,
      });
      toast.success("Transfer received — vehicle now at " + transfer.to_branch);
      onSaved();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="receive-form-modal">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
        <div className="font-heading text-xl font-semibold mb-4">Receive Vehicle</div>
        <div className="bg-slate-50 rounded-md p-3 mb-4 text-sm">
          <div className="font-medium text-slate-900">{transfer.model}</div>
          <div className="text-xs text-slate-500 font-mono">{transfer.chassis_number}</div>
          <div className="text-xs text-slate-600 mt-1">
            {transfer.from_branch} → <span className="text-orange-600 font-medium">{transfer.to_branch}</span>
          </div>
          {transfer.remarks && (
            <div className="text-xs text-slate-500 mt-2 border-t border-slate-200 pt-2">
              <span className="font-semibold">Dispatch remarks:</span> {transfer.remarks}
            </div>
          )}
        </div>
        <label className="block mb-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Received By</div>
          <input required value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)}
            placeholder="Your name / staff name"
            className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm" data-testid="receive-by-input" />
        </label>
        <label className="block mb-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Receipt Remarks</div>
          <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)}
            rows={3}
            placeholder="Condition on arrival, missing items, damage notes…"
            className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm" data-testid="receive-remarks-input" />
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-sm border border-slate-300" data-testid="receive-cancel">Cancel</button>
          <button type="submit" disabled={saving} data-testid="receive-submit"
            className="px-4 py-2 rounded-md text-sm bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60">
            {saving ? "Saving…" : "Confirm Receipt"}
          </button>
        </div>
      </form>
    </div>
  );
}
